import React, { useState } from 'react';
import { Modal } from '../../common/Modal';
import { FileUploadZone } from '../upload/FileUploadZone';
import { ValidationErrors } from '../upload/ValidationErrors';
import { WarningMessage } from '../upload/WarningMessage';
import { useGuidesStore } from '../../../stores/guidesStore';
import { usePilgrimGuideStore } from '../../../stores/pilgrimGuideStore';
import { Download, Copy, Check } from 'lucide-react';
import * as XLSX from 'xlsx';
import toast from 'react-hot-toast';
import { motion } from 'framer-motion';
import { collection, query, where, getDocs, writeBatch, doc, getDoc } from 'firebase/firestore';
import { firestore } from '../../../lib/firebase/config';
import { normalizePassportNumber } from '../../../utils/passportUtils';
import { useLanguage } from '../../../contexts/LanguageContext'; // Added import

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

type ValidationResult = {
  isValid: boolean;
  errors: string[];
  pilgrimId?: string;
  pilgrimName?: string;
  currentGuideName?: string;
  newGuideName?: string;
  status?: string;
};

export const BulkTransferModal = ({ isOpen, onClose }: Props) => {
  const { translations } = useLanguage(); // Added useLanguage hook
  const [file, setFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [results, setResults] = useState<ValidationResult[]>([]);
  const [copiedGuide, setCopiedGuide] = useState<string | null>(null);
  const [availableGuides, setAvailableGuides] = useState<Array<{ id: string; name: string }>>([]);
  const { guides } = useGuidesStore();
  const { assignPendingLinkToGuide } = usePilgrimGuideStore();
  const userRole = localStorage.getItem('userRole');
  const userId = localStorage.getItem('userId');

  // Initialize available guides based on user role
  React.useEffect(() => {
    if (userRole === 'organizer' && userId) {
      // Filter guides for this organizer
      const organizerGuides = Object.values(guides).filter((g) => g.organizerId === userId);
      setAvailableGuides(organizerGuides.map((g) => ({ id: g.id, name: g.name })));
    } else {
      // For admin, show all guides
      setAvailableGuides(Object.values(guides).map((g) => ({ id: g.id, name: g.name })));
    }
  }, [guides]);

  const handleDownloadTemplate = () => {
    const template = [
      {
        identifier: '123456789',
        identifier_type: 'passport',
        new_guide_name: 'Guide Full Name',
      },
      {
        identifier: 'pilgrim@example.com',
        identifier_type: 'email',
        new_guide_name: 'Guide Full Name',
      },
    ];

    const ws = XLSX.utils.json_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Template');
    XLSX.writeFile(wb, 'bulk-transfer-template.xlsx');
  };

  const handleCopyGuideName = async (guideName: string) => {
    try {
      await navigator.clipboard.writeText(guideName);
      setCopiedGuide(guideName);
      setTimeout(() => setCopiedGuide(null), 2000);
      toast.success(translations?.bulkTransfer?.copySuccess || 'Guide name copied successfully');
    } catch (error) {
      toast.error(translations?.bulkTransfer?.copyError || 'Failed to copy guide name');
    }
  };

  const validateAndProcessRow = async (row: any): Promise<ValidationResult> => {
    const errors: string[] = [];
    const result: ValidationResult = {
      isValid: false,
      errors: [],
      status: 'pending',
    };

    try {
      // Validate identifier type
      if (!row.identifier_type || !['passport', 'email'].includes(row.identifier_type.toLowerCase())) {
        errors.push(translations?.bulkTransfer?.errors?.invalidIdentifierType || 'Invalid identifier type');
        result.errors = errors;
        return result;
      }

      // Validate identifier
      if (!row.identifier) {
        errors.push(translations?.bulkTransfer?.errors?.identifierRequired || 'Identifier is required');
        result.errors = errors;
        return result;
      }

      // Validate new guide name
      if (!row.new_guide_name) {
        errors.push(translations?.bulkTransfer?.errors?.newGuideNameRequired || 'New guide name is required');
        result.errors = errors;
        return result;
      }

      // Find pilgrim
      let pilgrimQuery;
      if (row.identifier_type === 'passport') {
        const normalizedPassport = normalizePassportNumber(row.identifier);
        pilgrimQuery = query(collection(firestore, 'Pilgrims'), where('Passport_Number', '==', normalizedPassport));
      } else {
        pilgrimQuery = query(collection(firestore, 'Pilgrims'), where('Email', '==', row.identifier.toUpperCase()));
      }

      const pilgrimSnapshot = await getDocs(pilgrimQuery);
      if (pilgrimSnapshot.empty) {
        errors.push(translations?.bulkTransfer?.errors?.pilgrimNotFound || 'Pilgrim not found');
        result.errors = errors;
        return result;
      }

      const pilgrimDoc = pilgrimSnapshot.docs[0];
      const pilgrimData = pilgrimDoc.data();

      // Find current link (exclude waiting identification)
      const linksSnapshot = await getDocs(query(
        collection(firestore, 'PilgrimGuideLinks'),
        where('pilgrimId', '==', pilgrimDoc.id)
      ));

      const eligibleLinks = linksSnapshot.docs
        .map(docSnap => ({ ref: docSnap.ref, data: docSnap.data() }))
        .filter(link => link.data.status !== 'waiting_identification');

      if (eligibleLinks.length === 0) {
        errors.push(translations?.bulkTransfer?.errors?.noApprovedLink || 'No editable link found for pilgrim');
        result.errors = errors;
        return result;
      }

      const currentLinkEntry =
        eligibleLinks.find(link => link.data.status === 'approved') || eligibleLinks[0];
      const currentLink = currentLinkEntry.data;
      const currentGuide = currentLink.guideId ? guides[currentLink.guideId] : null;

      // Find new guide
      const newGuide = Object.values(guides).find((g) => g.name === row.new_guide_name);
      if (!newGuide) {
        errors.push(translations?.bulkTransfer?.errors?.newGuideNotFound || 'New guide not found');
        result.errors = errors;
        return result;
      }

      // Check if new guide is different from current guide
      if (currentLink.guideId && newGuide.id === currentLink.guideId) {
        errors.push(translations?.bulkTransfer?.errors?.sameGuide || 'New guide is the same as the current guide');
        result.errors = errors;
        return result;
      }

      // Check if new guide belongs to the same organizer (for organizer role)
      if (userRole === 'organizer' && userId) {
        if (newGuide.organizerId !== userId) {
          errors.push(translations?.bulkTransfer?.errors?.guideNotBelongToOrganizer || 'New guide does not belong to your organization');
          result.errors = errors;
          return result;
        }
      }

      result.isValid = true;
      result.pilgrimId = pilgrimDoc.id;
      result.pilgrimName = pilgrimData.Name;
      result.currentGuideName =
        currentGuide?.name || currentLink.guideName || 'Unassigned';
      result.newGuideName = newGuide.name;

      const isUnassigned = !currentLink.guideId;
      if (isUnassigned) {
        await assignPendingLinkToGuide(
          currentLinkEntry.ref.id,
          pilgrimDoc.id,
          newGuide.id,
          translations
        );
        result.status = 'success';
        return result;
      }

      // Perform the transfer
      const batch = writeBatch(firestore);
      const organizerRef = doc(firestore, 'Organizers', newGuide.organizerId);
      const organizerDoc = await getDoc(organizerRef);
      const organizerName = organizerDoc.exists() ? organizerDoc.data().name : 'Unknown Organizer';

      // 1. Update link
      batch.update(currentLinkEntry.ref, {
        guideId: newGuide.id,
        guideName: newGuide.name,
        organizerId: newGuide.organizerId,
        organizerName,
        updatedAt: Date.now(),
      });

      // 2. Update ownership if exists
      const ownershipRef = collection(firestore, 'PilgrimOwnership');
      const ownershipQuery = query(ownershipRef, where('pilgrimId', '==', pilgrimDoc.id));
      const ownershipSnapshot = await getDocs(ownershipQuery);

      if (!ownershipSnapshot.empty) {
        const ownershipDoc = ownershipSnapshot.docs[0];
        const oldData = ownershipDoc.data();

        batch.update(ownershipDoc.ref, {
          realGuideId: newGuide.id,
          ownerGuideId: newGuide.id,
          updatedAt: Date.now()
        });
      }
      
      // Update PilgrimSelector
      const selectorQuery = query(collection(firestore, 'PilgrimSelector'), where('pilgrimId', '==', pilgrimDoc.id));
      const selectorSnapshot = await getDocs(selectorQuery);
      
      if (!selectorSnapshot.empty) {
        batch.update(selectorSnapshot.docs[0].ref, {
          guideId: newGuide.id,
          guideName: newGuide.name,
          organizerId: newGuide.organizerId,
          organizerName,
          'guideData.name': newGuide.name,
          'guideData.organizerId': newGuide.organizerId,
          'organizerData.name': organizerName,
          updatedAt: Date.now()
        });
      }
      
      // Update operationlist - Importantly: preserve operator info
      const operationQuery = query(
        collection(firestore, 'operationlist'), 
        where('pilgrimId', '==', pilgrimDoc.id),
        where('guideId', '==', currentLink.guideId)
      );
      const operationSnapshot = await getDocs(operationQuery);
      
      if (!operationSnapshot.empty) {
        operationSnapshot.docs.forEach(doc => {
          const operationData = doc.data();
          // Preserve operator fields
          const operatorId = operationData.operatorId;
          const operatorName = operationData.operatorName;
          const operatorOrganizerId = operationData.operatorOrganizerId;
          const operatorOrganizerName = operationData.operatorOrganizerName;
          
          batch.update(doc.ref, {
            guideId: newGuide.id,
            guideName: newGuide.name,
            organizerId: newGuide.organizerId,
            organizerName,
            // Preserve operator fields
            operatorId,
            operatorName,
            operatorOrganizerId,
            operatorOrganizerName,
            updatedAt: Date.now()
          });
        });
      }

      await batch.commit();

      result.status = 'success';
    } catch (error) {
      console.error('Error processing row:', error);
      result.errors.push(error.message || 'Unknown error');
      result.status = 'error';
    }

    return result;
  };

  const handleUpload = async () => {
    if (!file) return;

    setIsLoading(true);
    setValidationErrors([]);
    setResults([]);

    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const data = e.target?.result;
          const workbook = XLSX.read(data, { type: 'binary' });
          const worksheet = workbook.Sheets[workbook.SheetNames[0]];
          const rows = XLSX.utils.sheet_to_json(worksheet) as any[];

          const validationResults: ValidationResult[] = [];

          // Process rows sequentially
          for (const row of rows) {
            const result = await validateAndProcessRow(row);
            validationResults.push(result);
            setResults((prev) => [...prev, result]); // Update results in real-time
          }

          // Export results
          const exportData = validationResults.map((result) => ({
            pilgrim_name: result.pilgrimName || '',
            current_guide: result.currentGuideName || '',
            new_guide: result.newGuideName || '',
            status: result.status,
            errors: result.errors.join(', '),
          }));

          const ws = XLSX.utils.json_to_sheet(exportData);
          
          // Add Arabic headers
          XLSX.utils.sheet_add_aoa(ws, [[
            'اسم الحاج',
            'المرشد الحالي',
            'المرشد الجديد', 
            'الحالة',
            'الأخطاء'
          ]], { origin: 'A1' });

          // Set column widths
          ws['!cols'] = [
            { wch: 30 }, // pilgrim name
            { wch: 25 }, // current guide
            { wch: 25 }, // new guide
            { wch: 15 }, // status
            { wch: 40 }, // errors
          ];
          const wb = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(wb, ws, 'Results');
          XLSX.writeFile(wb, 'bulk-transfer-results.xlsx');

          const successCount = validationResults.filter((r) => r.status === 'success').length;
          toast.success(
            `${successCount} ${translations?.bulkTransfer?.successMessage || 'pilgrims transferred successfully out of'} ${rows.length}`
          );
        } catch (error: any) {
          console.error('Error processing file:', error);
          setValidationErrors([error.message]);
        } finally {
          setIsLoading(false);
        }
      };

      reader.readAsBinaryString(file);
    } catch (error: any) {
      console.error('Error uploading file:', error);
      setValidationErrors([error.message]);
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={translations?.bulkTransfer?.title || 'Bulk Transfer Pilgrims'}>
      <div className="space-y-6">
        {/* Available Guides - Only show for organizer */}
        {userRole === 'organizer' && (
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium mb-3">{translations?.bulkTransfer?.availableGuides || 'Available Guides'}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {availableGuides.map((guide) => (
                <motion.div
                  key={guide.id}
                  whileHover={{ scale: 1.02 }}
                  className="bg-white p-3 rounded-lg shadow-sm"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{guide.name}</span>
                    <button
                      onClick={() => handleCopyGuideName(guide.name)}
                      className="p-1.5 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100"
                    >
                      {copiedGuide === guide.name ? (
                        <Check size={16} className="text-green-500" />
                      ) : (
                        <Copy size={16} />
                      )}
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* Template Download Button */}
        <button
          onClick={handleDownloadTemplate}
          className="flex items-center gap-2 text-primary-600 hover:text-primary-700"
        >
          <Download size={20} />
          <span>{translations?.bulkTransfer?.downloadTemplate || 'Download Excel Template'}</span>
        </button>

        {/* File Upload */}
        <FileUploadZone
          file={file}
          onFileSelect={setFile}
          onFileRemove={() => {
            setFile(null);
            setValidationErrors([]);
            setResults([]);
          }}
        />

        <ValidationErrors errors={validationErrors} />

        {/* Instructions */}
        <div className="flex items-start gap-3 bg-blue-50 text-blue-700 p-4 rounded-lg">
          <div className="text-sm">
            <p className="font-medium mb-2">{translations?.bulkTransfer?.instructionsTitle || 'Instructions'}</p>
            <ul className="list-disc list-inside space-y-1">
              <li>{translations?.bulkTransfer?.instructions?.downloadTemplate || 'Download the template and fill in the required information'}</li>
              <li>{translations?.bulkTransfer?.instructions?.searchByIdentifier || 'Search by passport number or email'}</li>
              <li>{translations?.bulkTransfer?.instructions?.copyGuideNames || 'Use the copy buttons above to easily copy guide names'}</li>
              <li>{translations?.bulkTransfer?.instructions?.approvedLinkRequired || 'Pilgrim must be linked to a guide and approved'}</li>
              <li>{translations?.bulkTransfer?.instructions?.ownershipUpdated || 'Ownership records will be updated automatically'}</li>
              <li>{translations?.bulkTransfer?.instructions?.operationsUpdated || 'Operation list entries will be updated automatically'}</li>
              <li>{translations?.bulkTransfer?.instructions?.selectorUpdated || 'Selector entries will be updated automatically'}</li>
            </ul>
          </div>
        </div>

        {/* Results Table */}
        {results.length > 0 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium">{translations?.bulkTransfer?.resultsTitle || 'Processing Results'}</h3>
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-500">
                  {results.filter((r) => r.status === 'success').length} / {results.length}{' '}
                  {translations?.bulkTransfer?.processed || 'processed'}
                </span>
                <button
                  onClick={() => {
                    // Export results
                    const exportData = results.map((result) => ({
                      pilgrim_name: result.pilgrimName || '',
                      current_guide: result.currentGuideName || '',
                      new_guide: result.newGuideName || '',
                      status: result.status,
                      errors: result.errors.join(', '),
                    }));

                    const ws = XLSX.utils.json_to_sheet(exportData);
                    const wb = XLSX.utils.book_new();
                    XLSX.utils.book_append_sheet(wb, ws, 'Results');
                    XLSX.writeFile(wb, 'bulk-transfer-results.xlsx');
                  }}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm bg-primary-50 text-primary-600 rounded-lg hover:bg-primary-100 transition-colors"
                >
                  <Download size={16} />
                  <span>{translations?.bulkTransfer?.downloadResults || 'Download Results'}</span>
                </button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500">
                      {translations?.bulkTransfer?.tableHeaders?.pilgrimName || 'Pilgrim Name'}
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500">
                      {translations?.bulkTransfer?.tableHeaders?.currentGuide || 'Current Guide'}
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500">
                      {translations?.bulkTransfer?.tableHeaders?.newGuide || 'New Guide'}
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500">
                      {translations?.bulkTransfer?.tableHeaders?.status || 'Status'}
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500">
                      {translations?.bulkTransfer?.tableHeaders?.errors || 'Errors'}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {results.map((result, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm">{result.pilgrimName || '-'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">{result.currentGuideName || '-'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">{result.newGuideName || '-'}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            result.status === 'success'
                              ? 'bg-green-100 text-green-800'
                              : result.status === 'error'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}
                        >
                          {result.status === 'success'
                            ? translations?.bulkTransfer?.statusSuccess || 'Transferred'
                            : result.status === 'error'
                            ? translations?.bulkTransfer?.statusError || 'Error'
                            : translations?.bulkTransfer?.statusPending || 'Processing'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-red-600">{result.errors.join(', ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:text-gray-800">
            {translations?.bulkTransfer?.cancelButton || 'Cancel'}
          </button>
          <button
            onClick={handleUpload}
            disabled={isLoading || !file}
            className={`px-6 py-2 rounded-lg text-white ${
              !file || isLoading ? 'bg-gray-400 cursor-not-allowed' : 'bg-primary-500 hover:bg-primary-600'
            }`}
          >
            {isLoading
              ? translations?.bulkTransfer?.processingButton || 'Processing...'
              : translations?.bulkTransfer?.transferButton || 'Transfer Pilgrims'}
          </button>
        </div>
      </div>
    </Modal>
  );
};
